import { type ReactNode } from 'react';
import NextLink from 'next/link';

import { WhitePanel } from 'components/Toolkit/WhitePanel/WhitePanel';
import { LinkButton } from 'components/Toolkit/Button/LinkButton';
import { MiniUserProfile } from 'components/Toolkit/MiniUserProfile/MiniUserProfile';

import { VerifiedList } from 'components/PageWrappers/AdDetails/components/VerifiedList/VerifiedList';
import { Verifications } from 'components/PageWrappers/AdDetails/components/Verifications/Verifications';
import {
  SELLER_TYPES,
  type ReservationStatuses,
  type UserRating,
  type UserVerification,
  SellerTypes,
} from 'components/PageWrappers/AdDetails/types';
import { StarRatingGroup } from 'components/PageWrappers/AdDetails/components/GoogleReviews/StarRatingGroup/StarRatingGroup';

import * as Styled from 'components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel.styled';
import { VerifiedTitle } from 'components/PageWrappers/AdDetails/components/Verifications/VerifiedTitle/VerifiedTitle';
import { DISPLAY_RESERVATION, FEATURE_FEEDBACK } from 'utils';
import { PrivateSellerRatings } from 'components/PageWrappers/AdDetails/components/SideSellerInfoPanel/SideSellerInfoPanel.styled';
import { Spacer } from 'helpers/genericStyles';
import { ReserveCTA } from '../ReserveCTA/ReserveCTA';

export interface SideSellerInfoPanelProps {
  title?: string;
  renderSendMessage?: ReactNode;
  renderShowPhone?: ReactNode;
  renderMakeOffer?: ReactNode;
  county?: string;
  type?: SellerTypes;
  sellerUrl?: string;
  isTrader: boolean;
  verification?: UserVerification;
  className?: string;
  testId?: string;
  isDealer?: boolean;
  showroomUrl?: string;
  illustration?: string;
  dealerReviews?: string;
  dealerRating?: string;
  userRating?: UserRating;
  membershipDuration?: string;
  onClickRatings?: () => void;
  onClickReviews?: () => void;
  onClickTitle?: () => void;
  reservationStatus?: ReservationStatuses;
}

const SideSellerInfoPanel = (props: SideSellerInfoPanelProps) => {
  const {
    title,
    renderSendMessage,
    renderShowPhone,
    renderMakeOffer,
    county,
    type = SELLER_TYPES.PRIVATE,
    sellerUrl,
    isTrader,
    verification,
    className,
    testId,
    isDealer,
    showroomUrl,
    illustration,
    dealerReviews,
    dealerRating,
    userRating,
    membershipDuration,
    onClickRatings,
    onClickReviews,
    onClickTitle,
    reservationStatus,
  } = props;

  const DISPLAY_USER_RATING = !isDealer && FEATURE_FEEDBACK;

  const viewAllAdsDisplayText = {
    [SELLER_TYPES.TRADER]: 'View our shop',
    [SELLER_TYPES.DEALER]: 'Visit showroom',
    [SELLER_TYPES.PRIVATE]: 'View all ads',
  };

  const renderVerificationComponent = (verification?: UserVerification) => {
    if (!verification || isDealer) return null;

    if (DISPLAY_USER_RATING) {
      return <Verifications verifications={verification} />;
    }

    return <VerifiedList verification={verification} />;
  };

  return (
    <WhitePanel className={className} testId={testId}>
      <Styled.MiniUserProfileAndVerifiedListContainer
        googleReviews={dealerReviews}
      >
        {title && (
          <MiniUserProfile
            title={
              DISPLAY_USER_RATING && verification ? (
                <VerifiedTitle
                  title={title}
                  verifications={verification}
                  onClickTitle={onClickTitle}
                />
              ) : (
                <LinkButton
                  ofType="SECONDARY"
                  fontSize="L18"
                  onClick={onClickTitle}
                >
                  {title}
                </LinkButton>
              )
            }
            illustration={illustration}
            county={county}
            type={type}
            isTrader={isTrader}
            verification={verification}
            isDealer={isDealer}
            position="SIDE"
          />
        )}

        <Spacer marginBottom="M16" />

        {renderVerificationComponent(verification)}
      </Styled.MiniUserProfileAndVerifiedListContainer>
      {dealerReviews && (
        <Styled.DealerRatings>
          {dealerRating && (
            <Styled.DealerReviewInfoTitle
              title={''}
              listItems={[
                <StarRatingGroup
                  key={1}
                  rating={parseFloat(dealerRating)}
                  totalReviews={dealerReviews}
                  clickTargetElementId="dealer-panel"
                  starWidth="20"
                  starHeight="20"
                  totalReviewsTrackingId={'google-reviews-total-reviews'}
                />,
              ]}
            />
          )}
        </Styled.DealerRatings>
      )}

      {DISPLAY_USER_RATING && onClickReviews && onClickRatings && (
        <PrivateSellerRatings
          membershipDuration={membershipDuration}
          userRating={userRating}
          onClickRatings={onClickRatings}
          onClickReviews={onClickReviews}
        />
      )}

      <Styled.ActionsList>
        {renderMakeOffer && <li>{renderMakeOffer}</li>}
        {renderSendMessage && <li>{renderSendMessage}</li>}
        {renderShowPhone && <li>{renderShowPhone}</li>}
        {reservationStatus && DISPLAY_RESERVATION && (
          <li>
            <ReserveCTA reservationStatus={reservationStatus} />
          </li>
        )}
      </Styled.ActionsList>
      {sellerUrl && (
        <>
          <Spacer marginBottom="M16" />
          <Styled.VisitAdsLink
            href={showroomUrl ?? sellerUrl}
            ofType="SECONDARY"
            data-tracking="view-all-ads-button"
            rel={isDealer ? undefined : 'nofollow'}
            underline
            NextLink={NextLink}
          >
            {viewAllAdsDisplayText[type]}
          </Styled.VisitAdsLink>
        </>
      )}
    </WhitePanel>
  );
};

export { SideSellerInfoPanel };
